/**
 * Universal header search across demands, debtors, payments, messages, and evidence.
 */
import { matchesMatterQuery } from './matterSearch.js';
import { debtorDisplayName, debtorCompanyPrimaryName } from './debtorIdentity.js';
import { formatMatterMoney } from './matterCurrency.js';
import { encodeDebtorId } from './debtorRecords.js';
import { matterDemandId } from './demandId.js';
import {
  listPaymentRecords,
  searchPaymentRecords,
  paymentDemandHref,
} from './paymentRecords.js';
import { listDemandConversations } from './communicationsStore.js';
import { collectEvidenceVaultFiles } from './evidenceVault.js';
import { matterAccountNumber, matterInvoiceNumber } from './messagesInbox.js';

export const UNIVERSAL_SEARCH_HINT =
  'Search demands, debtors, payments, messages, and evidence.';

export const SEARCH_GROUP_LIMIT = 4;

function normalize(value) {
  return String(value || '')
    .trim()
    .toLowerCase();
}

function fieldMatch(fields, q) {
  return fields.some((field) => normalize(field).includes(q));
}

export function matchesDebtorQuery(debtor, query) {
  const q = normalize(query);
  if (!q || !debtor) return false;
  return fieldMatch(
    [
      debtor.name,
      debtor.company,
      debtor.email,
      debtor.accountNumber,
      debtor.state,
      debtor.city,
      debtor.street,
      debtor.postalCode,
      debtor.country,
      debtor.id,
    ],
    q
  );
}

export function matchesMessageThreadQuery(thread, query) {
  const q = normalize(query);
  if (!q || !thread) return false;
  const matter = thread.matter;
  return fieldMatch(
    [
      matter?.Debtor_Name,
      matter?.Debtor_Company,
      matter?.Attorney_Name,
      matter?.Demand_ID,
      matterAccountNumber(matter),
      matterInvoiceNumber(matter),
      thread.preview,
      thread.matterId,
      matterDemandId(matter),
    ],
    q
  );
}

export function matchesEvidenceFileQuery(file, query) {
  const q = normalize(query);
  if (!q || !file) return false;
  return fieldMatch(
    [file.name, file.debtorName, file.matterLabel, file.demandId, file.kind, file.matterId],
    q
  );
}

function demandHit(matter) {
  const demandId = matterDemandId(matter);
  const title = debtorDisplayName(matter) || debtorCompanyPrimaryName(matter) || demandId || 'Demand';
  const subtitle = [demandId, formatMatterMoney(matter.Amount_Owed, matter), matter.Status]
    .filter(Boolean)
    .join(' · ');
  return {
    type: 'demand',
    id: String(matter.ROWID),
    title,
    subtitle,
    href: `/client/demands/${encodeURIComponent(matter.ROWID)}`,
    matter,
  };
}

function debtorHit(debtor) {
  const primary = debtor.company || debtor.name || 'Debtor';
  const secondary =
    debtor.name && debtor.company && debtor.name !== debtor.company ? debtor.name : '';
  return {
    type: 'debtor',
    id: String(debtor.id),
    title: primary,
    subtitle: [secondary, debtor.accountNumber, debtor.state].filter(Boolean).join(' · '),
    href: `/client/debtors/${encodeDebtorId(debtor.id)}`,
  };
}

function paymentHit(row) {
  return {
    type: 'payment',
    id: String(row.id),
    title: `${row.amountLabel} · ${row.debtorName || 'Payment'}`,
    subtitle: [row.demandId, row.reference, row.paidAtLabel, row.syncLabel]
      .filter(Boolean)
      .join(' · '),
    href: paymentDemandHref(row),
  };
}

function messageHit(thread) {
  const matter = thread.matter;
  const title =
    debtorCompanyPrimaryName(matter) ||
    matterDemandId(matter) ||
    `Demand ${thread.matterId}`;
  return {
    type: 'message',
    id: String(thread.matterId),
    title,
    subtitle: thread.preview || `${thread.messageCount || 0} message${thread.messageCount === 1 ? '' : 's'}`,
    href: `/client/messages?demand=${encodeURIComponent(thread.matterId)}`,
  };
}

function evidenceHit(file) {
  const demandHref =
    file.matterId && !file.demandDeleted
      ? `/client/demands/${encodeURIComponent(file.matterId)}`
      : '/client/evidence-locker';
  return {
    type: 'evidence',
    id: String(file.id),
    title: file.name || 'File',
    subtitle: [file.debtorName, file.demandId, file.kind].filter(Boolean).join(' · '),
    href: demandHref,
  };
}

/**
 * @param {{ matters?: any[], debtors?: any[], query?: string, limitPerGroup?: number }} input
 * @returns {{ query: string, groups: { id: string, label: string, hits: any[], viewAllHref: string|null }[], total: number }}
 */
export function searchUniversalRecords({
  matters = [],
  evidenceMatters = null,
  debtors = [],
  query = '',
  limitPerGroup = SEARCH_GROUP_LIMIT,
} = {}) {
  const q = String(query || '').trim();
  if (!q) {
    return { query: '', groups: [], total: 0 };
  }

  const demandHits = (matters || [])
    .filter((matter) => matchesMatterQuery(matter, q))
    .slice(0, limitPerGroup)
    .map(demandHit);

  const debtorHits = (debtors || [])
    .filter((debtor) => matchesDebtorQuery(debtor, q))
    .slice(0, limitPerGroup)
    .map(debtorHit);

  const paymentHits = searchPaymentRecords(listPaymentRecords(matters), q)
    .slice(0, limitPerGroup)
    .map(paymentHit);

  const messageHits = listDemandConversations(matters)
    .filter((thread) => thread.messageCount > 0 && matchesMessageThreadQuery(thread, q))
    .slice(0, limitPerGroup)
    .map(messageHit);

  const evidenceHits = collectEvidenceVaultFiles(evidenceMatters || matters)
    .filter((file) => matchesEvidenceFileQuery(file, q))
    .slice(0, limitPerGroup)
    .map(evidenceHit);

  const encoded = encodeURIComponent(q);
  const groups = [
    {
      id: 'demands',
      label: 'Demands',
      hits: demandHits,
      viewAllHref: `/client/demands?q=${encoded}`,
    },
    {
      id: 'debtors',
      label: 'Debtors',
      hits: debtorHits,
      viewAllHref: '/client/debtors',
    },
    {
      id: 'payments',
      label: 'Payments',
      hits: paymentHits,
      viewAllHref: `/client/payments?q=${encoded}`,
    },
    {
      id: 'messages',
      label: 'Messages',
      hits: messageHits,
      viewAllHref: `/client/messages?q=${encoded}`,
    },
    {
      id: 'evidence',
      label: 'Evidence',
      hits: evidenceHits,
      viewAllHref: '/client/evidence-locker',
    },
  ].filter((group) => group.hits.length > 0);

  return {
    query: q,
    groups,
    total: groups.reduce((sum, group) => sum + group.hits.length, 0),
  };
}
